import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { 
  ArrowLeft, 
  Ticket, 
  DollarSign, 
  Shield, 
  CheckCircle, 
  AlertTriangle,
  Copy,
  Download,
  QrCode,
  Eye,
  EyeOff,
  Lock,
  Unlock,
  Info,
  Clock,
  Calendar,
  Star,
  Crown,
  Gem,
  Zap,
  AlertCircle,
  CreditCard,
  Key,
  CheckSquare
} from 'lucide-react';
import { TicketClass, TicketClassInfo, Ticket as TicketType } from '../../types/ticket.types';
import PinSetupModal from './PinSetupModal';
import PinValidationModal from './PinValidationModal';
import useAutoScroll from '../../hooks/useAutoScroll';

// Styled Components
const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  color: white;
  background: linear-gradient(135deg, #1e3c72 0%, #2a5298 100%);
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 15px;
  margin-bottom: 30px;
  padding: 20px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.2);
`;

const BackButton = styled.button`
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: white;
  padding: 12px;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    background: rgba(255, 255, 255, 0.2);
    transform: scale(1.05);
  }
`;

const Title = styled.h1`
  font-size: 2.2rem;
  font-weight: 700;
  margin: 0;
  color: #ffd700;
  display: flex;
  align-items: center;
  gap: 15px;
`;

const Content = styled.div`
  flex: 1;
  padding: 0 20px 20px;
  overflow-y: auto;
`;

const StepIndicator = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 30px;
  gap: 20px;
`;

const Step = styled.div<{ isActive: boolean; isCompleted: boolean }>`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 20px;
  border-radius: 25px;
  background: ${props => {
    if (props.isCompleted) return 'linear-gradient(45deg, #4caf50, #8bc34a)';
    if (props.isActive) return 'rgba(255, 255, 255, 0.2)';
    return 'rgba(255, 255, 255, 0.1)';
  }};
  border: 2px solid ${props => {
    if (props.isCompleted) return '#4caf50';
    if (props.isActive) return '#ffd700';
    return 'rgba(255, 255, 255, 0.3)';
  }};
  color: ${props => props.isCompleted ? 'white' : props.isActive ? '#ffd700' : 'rgba(255, 255, 255, 0.7)'};
  font-weight: 600;
  transition: all 0.3s ease;
`;

const StepNumber = styled.div<{ isActive: boolean; isCompleted: boolean }>`
  width: 30px;
  height: 30px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${props => {
    if (props.isCompleted) return 'white';
    if (props.isActive) return '#ffd700';
    return 'rgba(255, 255, 255, 0.3)';
  }};
  color: ${props => props.isCompleted ? '#4caf50' : props.isActive ? '#333' : 'rgba(255, 255, 255, 0.7)'};
  font-weight: 700;
`;

const BalanceWarning = styled.div<{ hasInsufficientBalance: boolean }>`
  background: ${props => props.hasInsufficientBalance ? 'rgba(244, 67, 54, 0.2)' : 'rgba(76, 175, 80, 0.2)'};
  border: 1px solid ${props => props.hasInsufficientBalance ? 'rgba(244, 67, 54, 0.5)' : 'rgba(76, 175, 80, 0.5)'};
  border-radius: 15px;
  padding: 20px;
  margin-bottom: 30px;
  display: flex;
  align-items: center;
  gap: 15px;
  color: ${props => props.hasInsufficientBalance ? '#ffcdd2' : '#c8e6c9'};
`;

const WarningIcon = styled.div`
  font-size: 2rem;
  color: ${props => props.color || '#ff9800'};
`;

const WarningText = styled.div`
  flex: 1;
`;

const WarningTitle = styled.div`
  font-weight: 600;
  font-size: 1.1rem;
  margin-bottom: 5px;
`;

const WarningSubtitle = styled.div`
  font-size: 0.9rem;
  opacity: 0.8;
`;

const TicketClassesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 20px;
  margin-bottom: 30px;
`;

const TicketClassCard = styled.div<{ isSelected: boolean; color: string; disabled: boolean }>`
  background: ${props => {
    if (props.disabled) return 'rgba(255, 255, 255, 0.05)';
    if (props.isSelected) return 'rgba(255, 255, 255, 0.2)';
    return 'rgba(255, 255, 255, 0.1)';
  }};
  border: 2px solid ${props => {
    if (props.disabled) return 'rgba(255, 255, 255, 0.1)';
    if (props.isSelected) return props.color;
    return 'rgba(255, 255, 255, 0.2)';
  }};
  border-radius: 20px;
  padding: 25px;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  opacity: ${props => props.disabled ? 0.5 : 1};
  pointer-events: ${props => props.disabled ? 'none' : 'auto'};
  
  &:hover:not(:disabled) {
    transform: translateY(-5px);
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
  }
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: ${props => props.color};
  }
`;

const ClassHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;
`;

const ClassName = styled.h3<{ color: string }>`
  font-size: 1.5rem;
  font-weight: 700;
  margin: 0;
  color: ${props => props.color};
  text-transform: uppercase;
`;

const ClassIcon = styled.div<{ color: string }>`
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background: ${props => props.color};
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 1.5rem;
`;

const ClassFeatures = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0 0 20px 0;
`;

const ClassFeature = styled.li`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 8px;
  color: rgba(255, 255, 255, 0.9);
  font-size: 0.9rem;
  
  &::before {
    content: '✓';
    color: #4caf50;
    font-weight: bold;
  }
`;

const ClassPrice = styled.div`
  font-size: 1.3rem;
  font-weight: 700;
  color: #4caf50;
  text-align: center;
  padding: 15px;
  background: rgba(76, 175, 80, 0.1);
  border-radius: 15px;
  border: 1px solid rgba(76, 175, 80, 0.3);
`;

const InsufficientBalanceLabel = styled.div`
  background: rgba(244, 67, 54, 0.2);
  color: #ffcdd2;
  padding: 8px 12px;
  border-radius: 10px;
  font-size: 0.8rem;
  text-align: center;
  margin-top: 10px;
  border: 1px solid rgba(244, 67, 54, 0.3);
`;

const PinSection = styled.div`
  background: rgba(255, 255, 255, 0.1);
  border-radius: 20px;
  padding: 30px;
  margin-bottom: 30px;
  border: 1px solid rgba(255, 255, 255, 0.2);
`;

const PinTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: 700;
  margin: 0 0 20px 0;
  color: #ffd700;
  display: flex;
  align-items: center;
  gap: 15px;
`;

const PinInstructions = styled.div`
  background: rgba(33, 150, 243, 0.1);
  border: 1px solid rgba(33, 150, 243, 0.3);
  border-radius: 15px;
  padding: 20px;
  margin-bottom: 25px;
  color: #bbdefb;
  display: flex;
  align-items: center;
  font-size: 0.95rem;
  line-height: 1.5;
`;

const PinInputContainer = styled.div`
  display: flex;
  gap: 15px;
  justify-content: center;
  margin-bottom: 20px;
`;

const PinInput = styled.input`
  width: 60px;
  height: 60px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-radius: 15px;
  background: rgba(255, 255, 255, 0.1);
  color: white;
  font-size: 1.5rem;
  font-weight: 700;
  text-align: center;
  transition: all 0.3s ease;
  
  &:focus {
    outline: none;
    border-color: #ffd700;
    background: rgba(255, 255, 255, 0.2);
    transform: scale(1.05);
  }
  
  &::placeholder {
    color: rgba(255, 255, 255, 0.5);
  }
`;

const PinToggle = styled.button`
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: white;
  padding: 10px 20px;
  border-radius: 20px;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.9rem;
  
  &:hover {
    background: rgba(255, 255, 255, 0.2);
    transform: scale(1.05);
  }
`;

const PurchaseConfirmation = styled.div`
  background: rgba(255, 255, 255, 0.1);
  border-radius: 20px;
  padding: 30px;
  margin-bottom: 30px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  text-align: center;
`;

const ConfirmationTitle = styled.h3`
  font-size: 1.8rem;
  font-weight: 700;
  margin: 0 0 20px 0;
  color: #ffd700;
`;

const ConfirmationDetails = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 20px;
  margin-bottom: 30px;
`;

const ConfirmationItem = styled.div`
  background: rgba(255, 255, 255, 0.1);
  border-radius: 15px;
  padding: 20px;
  border: 1px solid rgba(255, 255, 255, 0.2);
`;

const ConfirmationLabel = styled.div`
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.7);
  margin-bottom: 8px;
  text-transform: uppercase;
  letter-spacing: 1px;
`;

const ConfirmationValue = styled.div`
  font-size: 1.3rem;
  font-weight: 700;
  color: white;
`;

const TicketPreview = styled.div`
  background: rgba(255, 255, 255, 0.1);
  border-radius: 20px;
  padding: 30px;
  margin-bottom: 30px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  text-align: center;
`;

const TicketHeader = styled.div`
  margin-bottom: 30px;
`;

const TicketTitle = styled.h3`
  font-size: 2rem;
  font-weight: 700;
  margin: 0 0 15px 0;
  color: #4caf50;
`;

const TicketSubtitle = styled.p`
  font-size: 1.1rem;
  color: rgba(255, 255, 255, 0.8);
  margin: 0;
`;

const TicketDetails = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 20px;
  margin-bottom: 30px;
`;

const DetailItem = styled.div`
  background: rgba(255, 255, 255, 0.1);
  border-radius: 15px;
  padding: 20px;
  border: 1px solid rgba(255, 255, 255, 0.2);
`;

const DetailValue = styled.div`
  font-size: 1.3rem;
  font-weight: 700;
  color: #ffd700;
  margin-bottom: 8px;
`;

const DetailLabel = styled.div`
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.7);
  text-transform: uppercase;
  letter-spacing: 1px;
`;

const TicketActions = styled.div`
  display: flex;
  gap: 15px;
  justify-content: center;
  flex-wrap: wrap;
`;

const ActionButton = styled.button<{ variant: 'primary' | 'secondary' }>`
  background: ${props => props.variant === 'primary' 
    ? 'linear-gradient(45deg, #4caf50, #8bc34a)' 
    : 'rgba(255, 255, 255, 0.1)'
  };
  border: 1px solid ${props => props.variant === 'primary' 
    ? 'rgba(76, 175, 80, 0.5)' 
    : 'rgba(255, 255, 255, 0.3)'
  };
  color: white;
  padding: 15px 30px;
  border-radius: 25px;
  cursor: pointer;
  transition: all 0.3s ease;
  font-weight: 600;
  font-size: 1rem;
  display: flex;
  align-items: center;
  gap: 10px;
  
  &:hover {
    transform: scale(1.05);
    box-shadow: 0 8px 25px rgba(76, 175, 80, 0.3);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
`;

const ContinueButton = styled.button<{ disabled: boolean }>`
  background: ${props => props.disabled 
    ? 'rgba(255, 255, 255, 0.1)' 
    : 'linear-gradient(45deg, #4caf50, #8bc34a)'
  };
  border: none;
  color: white;
  padding: 15px 40px;
  border-radius: 25px;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  transition: all 0.3s ease;
  font-weight: 600;
  font-size: 1.1rem;
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 0 auto;
  
  &:hover:not(:disabled) {
    transform: scale(1.05);
    box-shadow: 0 8px 25px rgba(76, 175, 80, 0.3);
  }
`;

// Ticket class information
const ticketClasses: TicketClassInfo[] = [
  {
    class: 'third',
    name: 'Third Class',
    minAmount: 100,
    maxAmount: 499,
    features: ['Basic competition access', 'Standard questions', 'Community support'],
    color: '#cd7f32',
    icon: '🥉'
  },
  {
    class: 'second',
    name: 'Second Class',
    minAmount: 500,
    maxAmount: 999,
    features: ['Enhanced competition access', 'Premium questions', 'Priority support'],
    color: '#c0c0c0',
    icon: '🥈'
  },
  {
    class: 'first',
    name: 'First Class',
    minAmount: 1000,
    maxAmount: 2499,
    features: ['VIP competition access', 'Expert questions', '24/7 support'],
    color: '#ffd700',
    icon: '🥇'
  },
  {
    class: 'premium',
    name: 'Premium',
    minAmount: 2500,
    maxAmount: 4999,
    features: ['Elite competition access', 'Master questions', 'Personal manager'],
    color: '#9c27b0',
    icon: '💎'
  },
  {
    class: 'vip',
    name: 'VIP',
    minAmount: 5000,
    maxAmount: 10000,
    features: ['Exclusive competition access', 'Legendary questions', 'Dedicated support'],
    color: '#ff5722',
    icon: '👑'
  }
];

interface TicketPurchaseProps {
  userBalance: number;
  onBack: () => void;
  onTicketPurchased: (ticket: TicketType) => void;
  onNavigateToTicketManagement?: () => void;
}

const TicketPurchase: React.FC<TicketPurchaseProps> = ({ 
  userBalance, 
  onBack, 
  onTicketPurchased,
  onNavigateToTicketManagement
}) => {
  // Ensure userBalance is always a valid number
  const safeUserBalance = typeof userBalance === 'number' && !isNaN(userBalance) ? userBalance : 0;
  console.log('TicketPurchase props:', { userBalance, safeUserBalance });
  const [currentStep, setCurrentStep] = useState(1);
  const [selectedClass, setSelectedClass] = useState<TicketClass | null>(null);
  const [pin, setPin] = useState(['', '', '', '']);
  const [showPin, setShowPin] = useState(false);
  const [generatedTicket, setGeneratedTicket] = useState<TicketType | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [showPinValidation, setShowPinValidation] = useState(false);
  const [userHasPin, setUserHasPin] = useState(false); // This would come from user profile
  const { scrollToContinueButton, scrollToBottom } = useAutoScroll();

  const totalSteps = 4; // Added PIN validation step
  const selectedClassInfo = selectedClass ? ticketClasses.find(c => c.class === selectedClass) : null;
  const hasInsufficientBalance = selectedClassInfo && safeUserBalance < (selectedClassInfo.minAmount || 0);
  const canProceedToPin = selectedClass && !hasInsufficientBalance;
  const canProceedToPurchase = selectedClass && pin.every(digit => digit !== '') && !hasInsufficientBalance;
  const canProceedToValidation = canProceedToPurchase && userHasPin;

  const handleClassSelect = (ticketClass: TicketClass) => {
    console.log('Ticket class selected:', ticketClass);
    setSelectedClass(ticketClass);
    
    // Auto-scroll to continue button after ticket class selection
    setTimeout(() => {
      scrollToContinueButton({ 
        behavior: 'smooth', 
        block: 'center',
        offset: 50 
      });
    }, 300);
  };

  const handlePinChange = (index: number, value: string) => {
    if (value.length <= 1 && /^\d*$/.test(value)) {
      const newPin = [...pin];
      newPin[index] = value;
      setPin(newPin);
      
      // Auto-focus next input
      if (value && index < 3) {
        const nextInput = document.getElementById(`pin-${index + 1}`);
        if (nextInput) nextInput.focus();
      }
    }
  };

  const handlePinKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !pin[index] && index > 0) {
      const prevInput = document.getElementById(`pin-${index - 1}`);
      if (prevInput) prevInput.focus();
    }
  };

  const handleContinue = () => {
    if (currentStep === 1 && canProceedToPin) {
      setCurrentStep(2);
    } else if (currentStep === 2 && canProceedToPurchase) {
      if (userHasPin) {
        setCurrentStep(3); // Go to PIN validation
      } else {
        setCurrentStep(4); // Go to purchase confirmation
      }
    } else if (currentStep === 3 && canProceedToValidation) {
      setCurrentStep(4); // Go to purchase confirmation
    }
  };

  const handlePinValidation = () => {
    setShowPinValidation(true);
  };

  const handlePinValidationComplete = (isValid: boolean) => {
    setShowPinValidation(false);
    if (isValid) {
      setCurrentStep(4); // Proceed to purchase confirmation
    }
  };

  const generateTicket = async () => {
    setIsProcessing(true);
    
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 2000));
    
    const ticket: TicketType = {
      id: `TKT-${Date.now()}`,
      userId: 'user1', // This would come from auth context
      ticketClass: selectedClass!,
      amount: selectedClassInfo?.minAmount || 0,
      status: 'active',
      purchaseDate: new Date(),
      expiryDate: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000), // 1 year
      ticketLink: `https://lavernai.com/ticket/${Date.now()}`,
      pin: pin.join(''),
      isActive: true,
      metadata: {
        lavernaiName: 'LavernAI Competition Ticket',
        amountStaked: selectedClassInfo?.minAmount || 0,
        timestamp: new Date().toISOString(),
        validationHash: `hash-${Date.now()}`,
        legalInfo: 'Valid for nationwide competitions',
        terms: 'Standard competition terms apply'
      }
    };
    
    setGeneratedTicket(ticket);
    setIsProcessing(false);
  };

  const handleCopyLink = async () => {
    if (generatedTicket) {
      try {
        await navigator.clipboard.writeText(generatedTicket.ticketLink);
        // Show success notification
        console.log('Ticket link copied to clipboard');
      } catch (err) {
        console.error('Failed to copy ticket link');
      }
    }
  };

  const handleDownloadTicket = () => {
    // Generate and download ticket PDF
    console.log('Downloading ticket...');
  };

  const handleFinish = () => {
    if (generatedTicket) {
      onTicketPurchased(generatedTicket);
    }
  };

  const getStepStatus = (step: number) => {
    if (step < currentStep) return 'completed';
    if (step === currentStep) return 'active';
    return 'pending';
  };

  const getStepTitle = (step: number) => {
    switch (step) {
      case 1: return 'Select Ticket Class';
      case 2: return 'Setup PIN';
      case 3: return 'Validate PIN';
      case 4: return 'Purchase Confirmation';
      default: return '';
    }
  };

  return (
    <Container>
      <Header>
        <BackButton onClick={onBack}>
          <ArrowLeft size={24} />
        </BackButton>
        <Title>
          <Ticket size={32} />
          Purchase Competition Ticket
        </Title>
      </Header>

      <Content>
        <StepIndicator>
          {Array.from({ length: totalSteps }, (_, index) => {
            const step = index + 1;
            const status = getStepStatus(step);
            return (
              <Step key={step} isActive={status === 'active'} isCompleted={status === 'completed'}>
                <StepNumber isActive={status === 'active'} isCompleted={status === 'completed'}>
                  {step}
                </StepNumber>
                <span>{getStepTitle(step)}</span>
              </Step>
            );
          })}
        </StepIndicator>

        {/* Balance Warning */}
        <BalanceWarning hasInsufficientBalance={hasInsufficientBalance}>
          <WarningIcon color={hasInsufficientBalance ? '#f44336' : '#4caf50'}>
            {hasInsufficientBalance ? <AlertCircle size={32} /> : <CheckCircle size={32} />}
          </WarningIcon>
          <WarningText>
            <WarningTitle>
              {hasInsufficientBalance ? 'Insufficient Balance' : 'Sufficient Balance'}
            </WarningTitle>
            <WarningSubtitle>
              {hasInsufficientBalance 
                ? `You need at least KES ${selectedClassInfo?.minAmount?.toLocaleString() || '0'} to purchase this ticket. Current balance: KES ${safeUserBalance.toLocaleString()}`
                : `Your current balance of KES ${safeUserBalance.toLocaleString()} is sufficient for this purchase.`
              }
            </WarningSubtitle>
          </WarningText>
        </BalanceWarning>
        


        {/* Step 1: Ticket Class Selection */}
        {currentStep === 1 && (
          <>
            <TicketClassesGrid>
              {ticketClasses.map((ticketClass) => {
                const isDisabled = safeUserBalance < (ticketClass.minAmount || 0);
                const isSelected = selectedClass === ticketClass.class;
                

                
                return (
                  <TicketClassCard
                    key={ticketClass.class}
                    isSelected={isSelected}
                    color={ticketClass.color}
                    disabled={isDisabled}
                    onClick={() => {
                      if (!isDisabled) {
                        handleClassSelect(ticketClass.class);
                      }
                    }}
                    className={`ticket-class-card ${ticketClass.class} ${isDisabled ? 'disabled' : ''} ${isSelected ? 'selected' : ''}`}
                  >
                    <ClassHeader>
                      <ClassName color={ticketClass.color}>{ticketClass.name}</ClassName>
                      <ClassIcon color={ticketClass.color}>{ticketClass.icon}</ClassIcon>
                    </ClassHeader>
                    
                    <ClassFeatures>
                      {ticketClass.features.map((feature, index) => (
                        <ClassFeature key={index}>{feature}</ClassFeature>
                      ))}
                    </ClassFeatures>
                    
                    <ClassPrice>
                      KES {ticketClass.minAmount?.toLocaleString() || '0'} - {ticketClass.maxAmount?.toLocaleString() || '0'}
                    </ClassPrice>
                    
                    {isDisabled && (
                      <InsufficientBalanceLabel>
                        Insufficient Balance
                      </InsufficientBalanceLabel>
                    )}
                  </TicketClassCard>
                );
              })}
            </TicketClassesGrid>
          </>
        )}

        {/* Step 2: PIN Setup */}
        {currentStep === 2 && (
          <>
            <PinSection>
              <PinTitle>
                <Key size={24} />
                Setup Your PIN
              </PinTitle>
              
              <PinInstructions>
                <Info size={16} style={{ marginRight: '8px' }} />
                This PIN will be required to use your ticket in competitions. 
                Keep it secure and don't share it with anyone.
              </PinInstructions>
              
              <PinInputContainer>
                {pin.map((digit, index) => (
                  <PinInput
                    key={index}
                    id={`pin-${index}`}
                    type={showPin ? 'text' : 'password'}
                    value={digit}
                    onChange={(e) => handlePinChange(index, e.target.value)}
                    onKeyDown={(e) => handlePinKeyDown(index, e)}
                    maxLength={1}
                    placeholder="0"
                  />
                ))}
              </PinInputContainer>
              
              <div style={{ textAlign: 'center', marginBottom: '20px' }}>
                <PinToggle onClick={() => setShowPin(!showPin)}>
                  {showPin ? <EyeOff size={16} /> : <Eye size={16} />}
                  {showPin ? ' Hide PIN' : ' Show PIN'}
                </PinToggle>
              </div>
            </PinSection>
          </>
        )}

        {/* Step 3: PIN Validation (if user already has PIN) */}
        {currentStep === 3 && userHasPin && (
          <>
            <PinSection>
              <PinTitle>
                <Shield size={24} />
                Enter Your PIN
              </PinTitle>
              
              <PinInstructions>
                <Info size={16} style={{ marginRight: '8px' }} />
                Please enter your existing PIN to continue with the purchase.
              </PinInstructions>
              
              <ActionButton variant="primary" onClick={handlePinValidation}>
                <Key size={18} />
                Enter PIN
              </ActionButton>
            </PinSection>
          </>
        )}

        {/* Step 4: Purchase Confirmation */}
        {currentStep === 4 && (
          <>
            <PurchaseConfirmation>
              <ConfirmationTitle>
                <CheckSquare size={32} />
                Confirm Your Purchase
              </ConfirmationTitle>
              
              <ConfirmationDetails>
                <ConfirmationItem>
                  <ConfirmationLabel>Ticket Class</ConfirmationLabel>
                  <ConfirmationValue>{selectedClassInfo?.name}</ConfirmationValue>
                </ConfirmationItem>
                <ConfirmationItem>
                  <ConfirmationLabel>Amount</ConfirmationLabel>
                  <ConfirmationValue>KES {selectedClassInfo?.minAmount?.toLocaleString() || '0'}</ConfirmationValue>
                </ConfirmationItem>
                <ConfirmationItem>
                  <ConfirmationLabel>PIN Status</ConfirmationLabel>
                  <ConfirmationValue>{userHasPin ? 'Using Existing' : 'New PIN Set'}</ConfirmationValue>
                </ConfirmationItem>
                <ConfirmationItem>
                  <ConfirmationLabel>Balance After</ConfirmationLabel>
                  <ConfirmationValue>KES {(safeUserBalance - (selectedClassInfo?.minAmount || 0)).toLocaleString()}</ConfirmationValue>
                </ConfirmationItem>
              </ConfirmationDetails>
              
              <ActionButton variant="primary" onClick={generateTicket} disabled={isProcessing}>
                <CreditCard size={18} />
                {isProcessing ? 'Processing...' : 'Confirm Purchase'}
              </ActionButton>
            </PurchaseConfirmation>
          </>
        )}

        {/* Generated Ticket Preview */}
        {generatedTicket && (
          <>
            <TicketPreview>
              <TicketHeader>
                <TicketTitle>🎉 Ticket Generated Successfully!</TicketTitle>
                <TicketSubtitle>
                  Your {selectedClassInfo?.name} ticket is ready. Copy the link below to join competitions.
                </TicketSubtitle>
              </TicketHeader>
              
              <TicketDetails>
                <DetailItem>
                  <DetailValue>{selectedClassInfo?.name}</DetailValue>
                  <DetailLabel>Ticket Class</DetailLabel>
                </DetailItem>
                <DetailItem>
                  <DetailValue>KES {generatedTicket.amount.toLocaleString()}</DetailValue>
                  <DetailLabel>Amount</DetailLabel>
                </DetailItem>
                <DetailItem>
                  <DetailValue>{generatedTicket.pin}</DetailValue>
                  <DetailLabel>PIN</DetailLabel>
                </DetailItem>
                <DetailItem>
                  <DetailValue>{generatedTicket.expiryDate.toLocaleDateString()}</DetailValue>
                  <DetailLabel>Expires</DetailLabel>
                </DetailItem>
              </TicketDetails>
              
              <TicketActions>
                <ActionButton variant="primary" onClick={handleCopyLink}>
                  <Copy size={18} />
                  Copy Link
                </ActionButton>
                <ActionButton variant="secondary" onClick={handleDownloadTicket}>
                  <Download size={18} />
                  Download
                </ActionButton>
                <ActionButton variant="secondary">
                  <QrCode size={18} />
                  QR Code
                </ActionButton>
              </TicketActions>
            </TicketPreview>
          </>
        )}

        {/* Continue Button */}
        {currentStep < 4 && (
          <ContinueButton
            id={`continue-step-${currentStep}`}
            disabled={!canProceedToPin || (currentStep === 2 && !canProceedToPurchase) || (currentStep === 3 && !canProceedToValidation)}
            onClick={handleContinue}
            className="continue-button"
          >
            {currentStep === 1 ? 'Continue to PIN Setup' : 
             currentStep === 2 ? 'Continue to Purchase' :
             'Continue to PIN Validation'}
          </ContinueButton>
        )}

        {/* Finish Button */}
        {currentStep === 4 && generatedTicket && (
          <ContinueButton disabled={false} onClick={handleFinish}>
            <CheckCircle size={20} />
            Finish & Go to Competitions
          </ContinueButton>
        )}
      </Content>

      {/* PIN Validation Modal */}
      {showPinValidation && (
        <PinValidationModal
          ticket={{
            id: 'temp',
            userId: 'user1',
            ticketClass: selectedClass!,
            amount: selectedClassInfo?.minAmount || 0,
            status: 'active',
            purchaseDate: new Date(),
            expiryDate: new Date(),
            ticketLink: '',
            pin: '',
            isActive: true,
            metadata: {
              lavernaiName: 'LavernAI Competition Ticket',
              amountStaked: selectedClassInfo?.minAmount || 0,
              timestamp: new Date().toISOString(),
              validationHash: '',
              legalInfo: '',
              terms: ''
            }
          }}
          onClose={() => setShowPinValidation(false)}
          onComplete={handlePinValidationComplete}
        />
      )}
    </Container>
  );
};

export default TicketPurchase;
